import React, { useState, useEffect } from 'react';
import {
  ShoppingCart,
  CreditCard,
  Package,
  Zap,
  ShieldCheck,
  Truck,
  Smartphone,
  CheckCircle2,
  ArrowRight,
  Globe,
  Layers,
  Cpu,
  Search,
  Lock,
  MessageSquare,
  ExternalLink,
  Sparkles,
  TrendingUp,
  Award,
  ArrowUpRight,
  RefreshCw,
  Sliders,
  DollarSign,
  BarChart3,
  MapPin,
  Users,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const EcommerceSolutions = () => {
  // Running Architecture Flow State
  const [activeArchStep, setActiveArchStep] = useState(0);
  const [isArchAutoPlaying, setIsArchAutoPlaying] = useState(true);
  const [activeStageFilter, setActiveStageFilter] = useState('all');

  const architectureStages = [
    { id: 'all', label: 'All 15 Nodes' },
    { id: 'Inbound Discovery', label: '1. Discovery' },
    { id: 'Catalog Engagement', label: '2. Catalog' },
    { id: 'Conversion & Checkout', label: '3. Checkout' },
    { id: 'Fulfillment & Logistics', label: '4. Fulfillment' },
    { id: 'Retention & Security', label: '5. Security & CRM' },
  ];

  const architectureNodes = [
    {
      id: 1,
      step: '01',
      phase: 'Inbound Discovery',
      title: '1. Store Sessions (Top of Funnel)',
      desc: 'Real-time visitor discovery and traffic attribution across ad, social, and direct channels.',
      keypoint: 'Channel Attribution & Bot Filtering',
      icon: Users,
      iconBg: 'bg-cyan-50 text-cyan-600 border-cyan-100',
      activeRing: 'ring-cyan-500 border-cyan-400'
    },
    {
      id: 2,
      step: '02',
      phase: 'Inbound Discovery',
      title: 'Most Visited Storefront Routes',
      desc: 'Route telemetry identifying top traversed hero banners, collections, and catalog paths.',
      keypoint: 'SPA Fast Routing & Funnel Telemetry',
      icon: Globe,
      iconBg: 'bg-purple-50 text-purple-600 border-purple-100',
      activeRing: 'ring-purple-500 border-purple-400'
    },
    {
      id: 3,
      step: '03',
      phase: 'Inbound Discovery',
      title: 'Real-Time Visitor Log & Telemetry',
      desc: 'Granular session logs tracking shopper geography, device viewports, and dwell times.',
      keypoint: 'IP Geolocation & UX Diagnostics',
      icon: BarChart3,
      iconBg: 'bg-blue-50 text-blue-600 border-blue-100',
      activeRing: 'ring-blue-500 border-blue-400'
    },
    {
      id: 4,
      step: '04',
      phase: 'Catalog Engagement',
      title: '2. Product Views (Catalog Engagement)',
      desc: 'Catalog browsing analytics tracking high-intent PDP views and variant interest.',
      keypoint: 'Sub-Second PDPs & Image Zoom',
      icon: Search,
      iconBg: 'bg-amber-50 text-amber-600 border-amber-100',
      activeRing: 'ring-amber-500 border-amber-400'
    },
    {
      id: 5,
      step: '05',
      phase: 'Catalog Engagement',
      title: 'Visual CMS & Slider Builder',
      desc: 'Drag-and-drop dashboard for hero sliders, collection banners, and promo badges.',
      keypoint: 'No-Code Updates & Instant Publishing',
      icon: Sliders,
      iconBg: 'bg-orange-50 text-orange-600 border-orange-100',
      activeRing: 'ring-orange-500 border-orange-400'
    },
    {
      id: 6,
      step: '06',
      phase: 'Catalog Engagement',
      title: 'Live Inventory & Catalog Sync',
      desc: 'Bi-directional stock sync preventing overselling across multi-variant SKUs.',
      keypoint: 'Low-Stock Alerts & Reservation Safety',
      icon: Package,
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      activeRing: 'ring-emerald-500 border-emerald-400'
    },
    {
      id: 7,
      step: '07',
      phase: 'Conversion & Checkout',
      title: 'Discount & Promotion Engine',
      desc: 'Automated rules for tiered cart discounts, promo coupons, and free shipping triggers.',
      keypoint: 'Cart Spend Rules & Dynamic Auto-Apply',
      icon: TrendingUp,
      iconBg: 'bg-rose-50 text-rose-600 border-rose-100',
      activeRing: 'ring-rose-500 border-rose-400'
    },
    {
      id: 8,
      step: '08',
      phase: 'Conversion & Checkout',
      title: 'Razorpay & 1-Click UPI Gateway',
      desc: '1-click mobile checkout supporting GPay, PhonePe, Paytm, Cards, and NetBanking.',
      keypoint: 'Instant UPI Intent & Webhook Verify',
      icon: Zap,
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      activeRing: 'ring-emerald-500 border-emerald-400'
    },
    {
      id: 9,
      step: '09',
      phase: 'Conversion & Checkout',
      title: '3. Paid Checkout Orders',
      desc: 'Frictionless checkout completion with zero payment dropouts and instant confirmation.',
      keypoint: 'Slide-Out Drawer & GST Invoices',
      icon: CreditCard,
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      activeRing: 'ring-emerald-500 border-emerald-400'
    },
    {
      id: 10,
      step: '10',
      phase: 'Fulfillment & Logistics',
      title: 'Live Fulfillment Pipeline',
      desc: 'End-to-end dispatch registry: Pending → Packing → In Transit → Delivered.',
      keypoint: '1-Click AWB & Shipping Label Sync',
      icon: Truck,
      iconBg: 'bg-cyan-50 text-cyan-600 border-cyan-100',
      activeRing: 'ring-cyan-500 border-cyan-400'
    },
    {
      id: 11,
      step: '11',
      phase: 'Fulfillment & Logistics',
      title: 'Pan-India Logistics & Pincode Sync',
      desc: '28,000+ pincode check with automated Shiprocket and Delhivery courier booking.',
      keypoint: 'PDP Pincode Check & Tracking SMS',
      icon: MapPin,
      iconBg: 'bg-blue-50 text-blue-600 border-blue-100',
      activeRing: 'ring-blue-500 border-blue-400'
    },
    {
      id: 12,
      step: '12',
      phase: 'Fulfillment & Logistics',
      title: 'Cart Recovery & Abandonment Engine',
      desc: 'Automated WhatsApp and email triggers to recover abandoned checkouts and lost sales.',
      keypoint: '1-Click Cart Links & Recovery Alerts',
      icon: RefreshCw,
      iconBg: 'bg-orange-50 text-orange-600 border-orange-100',
      activeRing: 'ring-orange-500 border-orange-400'
    },
    {
      id: 13,
      step: '13',
      phase: 'Retention & Security',
      title: 'Customer CRM & Review Registry',
      desc: 'Centralized customer database with multi-order purchase histories and verified reviews.',
      keypoint: 'Order History & Review Workflows',
      icon: ShieldCheck,
      iconBg: 'bg-amber-50 text-amber-600 border-amber-100',
      activeRing: 'ring-amber-500 border-amber-400'
    },
    {
      id: 14,
      step: '14',
      phase: 'Retention & Security',
      title: 'Cloudflare CDN & Edge Speed',
      desc: 'Global edge caching network delivering sub-second page loads and automated DDoS defense.',
      keypoint: 'WebP Compression & Edge Caching',
      icon: Cpu,
      iconBg: 'bg-cyan-50 text-cyan-600 border-cyan-100',
      activeRing: 'ring-cyan-500 border-cyan-400'
    },
    {
      id: 15,
      step: '15',
      phase: 'Retention & Security',
      title: 'PCI-DSS Level 1 Security',
      desc: 'Bank-grade encryption protecting payment card tokens and sensitive customer data.',
      keypoint: 'Zero-Retention Tokens & TLS 1.3',
      icon: Lock,
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      activeRing: 'ring-emerald-500 border-emerald-400'
    }
  ];

  const displayedNodes = activeStageFilter === 'all'
    ? architectureNodes
    : architectureNodes.filter((node) => node.phase === activeStageFilter);

  // Auto-play timer for running architecture
  useEffect(() => {
    if (!isArchAutoPlaying) return;
    const interval = setInterval(() => {
      setActiveArchStep((prev) => (prev + 1) % architectureNodes.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [isArchAutoPlaying, architectureNodes.length]);
  // Hero Mockup Slides
  const heroMockupSlides = [
    {
      title: 'Zubelin Paris — Luxury Artificial & Fashion Jewellery Store',
      url: 'zubelin.com',
      image: '/images/zubelin_live_hero.png',
      badge1: 'Live D2C Storefront',
      badge1Color: 'text-emerald-400',
      badge2Title: 'D2C Storefront',
      badge2Val: '24 SKUs • Live AOV ₹470'
    },
    {
      title: 'Multi-Product Catalog Storefront Page',
      url: 'store.hoasp.com/products/catalog',
      image: '/images/ecom_multi_product_catalog_mockup.png',
      badge1: '5,000+ Active Products',
      badge1Color: 'text-orange-400',
      badge2Title: 'Product Catalog UI',
      badge2Val: 'Instant AI Search & Filters'
    },
    {
      title: 'Frictionless Shopping Cart & Checkout Page',
      url: 'store.hoasp.com/shopping-cart',
      image: '/images/ecom_shopping_cart_checkout_mockup.png',
      badge1: '1-Click Checkout Active',
      badge1Color: 'text-cyan-400',
      badge2Title: 'Cart Conversion',
      badge2Val: 'Stripe, Apple Pay & UPI'
    },
    {
      title: 'Single Product View & Gallery Page',
      url: 'store.hoasp.com/product/view-details',
      image: '/images/ecom_single_product_view_mockup.png',
      badge1: 'Interactive 360° View',
      badge1Color: 'text-emerald-400',
      badge2Title: 'Product Detail Engine',
      badge2Val: 'Sub-0.8s PDP Speed'
    },
    {
      title: 'E-Commerce Admin & Sales Telemetry Dashboard',
      url: 'admin.hoasp.com/analytics-dashboard',
      image: '/images/ecom_analytics_dashboard_mockup.png',
      badge1: '$1.2M+ Real-Time GMV',
      badge1Color: 'text-amber-400',
      badge2Title: 'E-Commerce Telemetry',
      badge2Val: 'Live Sales & Revenue Metrics'
    }
  ];

  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % heroMockupSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroMockupSlides.length]);



  // Curated Core Engineering Capabilities (Zero redundancy)
  const coreCapabilities = [
    {
      icon: Smartphone,
      title: 'Headless Next.js & React Storefronts',
      badge: 'Sub-Second Speed',
      description: 'Decoupled modern storefronts delivering sub-1.0s Core Web Vitals, app-like mobile ergonomics, and instant route transitions.',
      highlights: ['Next.js 14 & React 18 SPA Architecture', 'Progressive Web App (PWA) Offline Cache', 'Google PageSpeed 95+ Mobile Score'],
      color: 'from-cyan-500 to-blue-600',
      borderColor: 'border-cyan-500/30'
    },
    {
      icon: CreditCard,
      title: 'Frictionless Razorpay & 1-Click Checkout',
      badge: 'Zero Cart Drop-Off',
      description: 'Slide-out cart drawer with instant UPI, Cards, NetBanking, and Buy Now Pay Later (BNPL) to minimize checkout abandonment.',
      highlights: ['Razorpay, Stripe & Apple Pay Integration', '1-Click Express Guest Checkout', 'PCI-DSS Level 1 Encrypted Compliance'],
      color: 'from-orange-500 to-amber-600',
      borderColor: 'border-orange-500/30'
    },
    {
      icon: Search,
      title: 'AI Product Discovery & Instant Search',
      badge: 'Conversion Engine',
      description: 'Intelligent auto-suggest search, multi-attribute filtering, and real-time personalized product recommendation algorithms.',
      highlights: ['Sub-50ms Predictive Auto-Suggest', 'Faceted Filters (Metal, Price, Occasion)', 'Personalized Cross-Selling Widgets'],
      color: 'from-amber-500 to-yellow-600',
      borderColor: 'border-amber-500/30'
    },
    {
      icon: Package,
      title: 'Omnichannel ERP & Warehouse Sync',
      badge: 'Operations Telemetry',
      description: 'Centralized stock telemetry synchronizing your online store, physical POS counters, and marketplaces in real-time.',
      highlights: ['Real-Time Multi-Warehouse Stock Sync', 'Amazon, Flipkart & eBay API Connectors', 'Automated Low-Stock Alert Engine'],
      color: 'from-emerald-500 to-teal-600',
      borderColor: 'border-emerald-500/30'
    },
    {
      icon: Truck,
      title: 'Automated Logistics & Courier Telemetry',
      badge: 'Shipping Automation',
      description: 'Automated courier sync with Shiprocket, Delhivery, FedEx, and DHL for real-time shipping rates and WhatsApp order notifications.',
      highlights: ['Automated Shipping Label Generation', 'Pincode Serviceability & Cash on Delivery', 'Real-Time WhatsApp Tracking Updates'],
      color: 'from-purple-500 to-indigo-600',
      borderColor: 'border-purple-500/30'
    },
    {
      icon: ShieldCheck,
      title: 'Flash Sale Scaling & Bot Shield',
      badge: 'Zero Downtime',
      description: 'High-availability Kubernetes architecture engineered to handle 100,000+ concurrent checkout requests during festive flash sales.',
      highlights: ['Cloudflare DDoS & Scraper Bot Shield', 'Dynamic Auto-Scaling Cloud Clusters', '99.99% Guaranteed Transaction Uptime'],
      color: 'from-rose-500 to-pink-600',
      borderColor: 'border-rose-500/30'
    }
  ];

  // Supported E-Commerce Platforms
  const platforms = [
    {
      name: 'Custom Next.js 14 / React SPA',
      desc: 'Bespoke headless storefronts with tailored UX design, maximum speed, and complete API flexibility.',
      badge: 'Recommended for D2C',
      tagColor: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30'
    },
    {
      name: 'Shopify & Shopify Plus',
      desc: 'Custom Liquid themes, private app extensions, headless Shopify Hydrogen, and automated checkout.',
      badge: 'Enterprise Retail',
      tagColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
    },
    {
      name: 'WooCommerce Enterprise',
      desc: 'Custom WordPress plugins, optimized database query indexing, and scalable high-traffic WooCommerce hosting.',
      badge: 'Open-Source Control',
      tagColor: 'text-amber-400 bg-amber-500/10 border-amber-500/30'
    },
    {
      name: 'Adobe Commerce (Magento)',
      desc: 'Enterprise B2B and B2C multi-store architecture with complex ERP workflows and multi-country pricing.',
      badge: 'Global Enterprise',
      tagColor: 'text-orange-400 bg-orange-500/10 border-orange-500/30'
    }
  ];


  return (
    <div className="min-h-screen text-left pt-20 relative bg-gray-950 text-gray-100 overflow-hidden">

      {/* Tech Grid Backdrop & Subtle Ambient Glows */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#38bdf80c_1px,transparent_1px),linear-gradient(to_bottom,#38bdf80c_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute top-10 left-1/4 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-[140px]" />
        <div className="absolute top-1/3 right-10 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[140px]" />
        <div className="absolute bottom-10 left-1/3 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[140px]" />
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 border-b border-gray-800/80 z-10 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6">

              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500/15 via-amber-500/15 to-cyan-500/15 border border-orange-500/30 rounded-full px-4 py-1.5 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                <span className="text-orange-400 text-xs font-mono font-bold uppercase tracking-wider">
                  ENTERPRISE E-COMMERCE ENGINEERING
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight drop-shadow-2xl">
                High-Converting <span className="bg-gradient-to-r from-cyan-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">Digital Storefronts</span> & Marketplaces
              </h1>

              <p className="text-base md:text-xl text-gray-300 leading-relaxed font-normal">
                From bespoke headless Next.js platforms to enterprise D2C stores, we engineer scalable e-commerce ecosystems built for sub-second speeds, frictionless Razorpay/Stripe checkouts, and maximum conversion rates.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-500 hover:from-orange-600 hover:to-cyan-600 text-white px-8 py-6 text-base font-extrabold rounded-xl shadow-xl hover:shadow-orange-500/30 transition-all duration-300 flex items-center justify-center gap-2">
                    Launch Your Digital Storefront
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <a href="#portfolio-zubelin" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-gray-700 hover:border-gray-500 text-gray-200 hover:bg-gray-900/80 px-8 py-6 text-base font-bold rounded-xl backdrop-blur-md flex items-center justify-center gap-2">
                    View Live Portfolio
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </Button>
                </a>
              </div>

            </div>

            {/* Right Interactive Mockup Telemetry Card */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-4 shadow-2xl backdrop-blur-xl relative overflow-hidden">

                {/* Browser Header Bar */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-950 rounded-2xl mb-3 border border-gray-800">
                  <div className="flex items-center space-x-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
                  </div>
                  <div className="bg-gray-900 text-cyan-400 text-[11px] font-mono px-4 py-1 rounded-lg border border-gray-800 truncate max-w-[200px]">
                    {heroMockupSlides[currentHeroSlide].url}
                  </div>
                  <Globe className="w-4 h-4 text-cyan-400" />
                </div>

                {/* E-Commerce Digital Image Slider Frame */}
                <div className="relative h-72 rounded-2xl overflow-hidden mb-3 border border-gray-800/80">
                  <img
                    src={heroMockupSlides[currentHeroSlide].image}
                    alt={heroMockupSlides[currentHeroSlide].title}
                    className="w-full h-full object-cover transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950/40 via-transparent to-transparent pointer-events-none" />
                </div>

                {/* Slider Navigation Bar & Dots */}
                <div className="flex items-center justify-between px-2 pt-1">
                  <span className="text-xs font-mono text-gray-300 font-semibold truncate pr-2">
                    {heroMockupSlides[currentHeroSlide].title}
                  </span>

                  <div className="flex space-x-1.5 flex-shrink-0">
                    {heroMockupSlides.map((_, dotIdx) => (
                      <button
                        key={dotIdx}
                        onClick={() => setCurrentHeroSlide(dotIdx)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          currentHeroSlide === dotIdx ? 'w-6 bg-cyan-400' : 'w-2 bg-gray-700 hover:bg-gray-500'
                        }`}
                        aria-label={`Go to slide ${dotIdx + 1}`}
                      />
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* E-Commerce Market Trends & Industry Benchmarks */}
      <section className="py-10 bg-gray-900/60 border-b border-gray-800/80 relative z-10 backdrop-blur-md">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
            <div>
              <div className="inline-flex items-center space-x-2 text-xs font-mono font-bold text-orange-400 uppercase tracking-wider mb-1">
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                <span>E-Commerce Industry Benchmarks & Market Trends</span>
              </div>
              <p className="text-gray-400 text-xs md:text-sm">
                Key macroeconomic retail data and consumer behavior shifts shaping modern digital commerce.
              </p>
            </div>
            <div className="text-[11px] font-mono text-gray-400 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span>Sources: Statista, Baymard Institute & McKinsey Digital</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Card 1: Market Expansion */}
            <div className="bg-gray-950/80 border border-gray-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden flex flex-col justify-between hover:border-cyan-500/40 transition-colors">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    MARKET EXPANSION
                  </span>
                  <span className="text-[11px] text-gray-400 font-mono">By 2026</span>
                </div>
                <div className="text-3xl lg:text-4xl font-black text-cyan-400 font-mono tracking-tight mb-1">
                  $8.1T
                </div>
                <div className="text-sm font-bold text-white mb-1">
                  Global Retail E-Commerce
                </div>
                <p className="text-xs text-gray-400 leading-relaxed font-normal">
                  Total worldwide digital retail sales projected to exceed $8.1 trillion as cross-border digital buying expands.
                </p>
              </div>
            </div>

            {/* Card 2: Mobile Commerce */}
            <div className="bg-gray-950/80 border border-gray-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden flex flex-col justify-between hover:border-orange-500/40 transition-colors">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    MOBILE COMMERCE
                  </span>
                  <span className="text-[11px] text-gray-400 font-mono">Dominant Channel</span>
                </div>
                <div className="text-3xl lg:text-4xl font-black text-orange-400 font-mono tracking-tight mb-1">
                  73%
                </div>
                <div className="text-sm font-bold text-white mb-1">
                  M-Commerce Share of Total Sales
                </div>
                <p className="text-xs text-gray-400 leading-relaxed font-normal">
                  Nearly 3 out of every 4 online orders happen on mobile devices, making sub-second mobile UX paramount.
                </p>
              </div>
            </div>

            {/* Card 3: Cart Abandonment */}
            <div className="bg-gray-950/80 border border-gray-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden flex flex-col justify-between hover:border-rose-500/40 transition-colors">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">
                    INDUSTRY BOTTLENECK
                  </span>
                  <span className="text-[11px] text-gray-400 font-mono">Baymard Benchmark</span>
                </div>
                <div className="text-3xl lg:text-4xl font-black text-rose-400 font-mono tracking-tight mb-1">
                  70.2%
                </div>
                <div className="text-sm font-bold text-white mb-1">
                  Average Cart Abandonment
                </div>
                <p className="text-xs text-gray-400 leading-relaxed font-normal">
                  Complex checkout flows cause over 70% cart abandonment; 1-click checkout and UPI reduce this drastically.
                </p>
              </div>
            </div>

            {/* Card 4: AI Personalization */}
            <div className="bg-gray-950/80 border border-gray-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden flex flex-col justify-between hover:border-emerald-500/40 transition-colors">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    AI IMPACT
                  </span>
                  <span className="text-[11px] text-gray-400 font-mono">McKinsey Insights</span>
                </div>
                <div className="text-3xl lg:text-4xl font-black text-emerald-400 font-mono tracking-tight mb-1">
                  +35%
                </div>
                <div className="text-sm font-bold text-white mb-1">
                  AOV Lift via Smart Discovery
                </div>
                <p className="text-xs text-gray-400 leading-relaxed font-normal">
                  Storefronts deploying AI search, personalized recommendations, and dynamic bundles see 35%+ revenue gains.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Client Portfolio & Live Case Study: Zubelin.com */}
      <section id="portfolio-zubelin" className="py-20 md:py-28 relative z-10 border-b border-gray-800/80">
        <div className="container mx-auto px-6">

          {/* Section Header */}
          <div className="text-center mb-14 max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500/15 to-rose-500/15 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4 shadow-lg backdrop-blur-md">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-amber-300 text-xs font-mono font-extrabold uppercase tracking-wider">
                FEATURED CLIENT PORTFOLIO & LIVE CASE STUDY
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
              Powering High-Growth D2C: <span className="bg-gradient-to-r from-amber-300 via-rose-400 to-cyan-400 bg-clip-text text-transparent">Zubelin.com</span>
            </h2>

            <p className="text-gray-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6">
              D2C digital storefront engineered for Zubelin Paris with sub-second page transitions, seamless Razorpay checkout, and automated Pan-India fulfillment.
            </p>
            {/* Live Storefront Link Strip */}
            <div className="inline-flex flex-wrap items-center justify-center gap-3 bg-gray-900/90 border border-gray-800 px-5 py-2.5 rounded-2xl shadow-xl backdrop-blur-md">
              <div className="flex items-center space-x-2 font-mono text-xs text-gray-300">
                <Lock className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400">https://</span>
                <span className="text-white font-bold">zubelin.com</span>
                <span className="text-gray-400 text-[10px] hidden sm:inline ml-1">• TLS 1.3 Active</span>
              </div>
              <span className="text-gray-700 hidden sm:inline">|</span>
              <a
                href="https://zubelin.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center space-x-1.5 text-xs font-bold text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                <span>Visit Live Storefront</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Running Architecture Control & Timeline Bar */}
          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-5 md:p-6 mb-10 shadow-2xl backdrop-blur-md">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-gray-800">
              {/* Live Signal Indicator */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  <span className="text-[11px] font-mono font-bold text-emerald-300 uppercase tracking-wider">
                    RUNNING ARCHITECTURE SIMULATION
                  </span>
                </div>
                <div className="hidden sm:flex items-center space-x-1.5 text-xs text-gray-400 font-mono">
                  <span>Active Flow:</span>
                  <span className="text-white font-bold">{architectureNodes[activeArchStep]?.title}</span>
                </div>
              </div>

              {/* Step Navigation & Play/Pause Controls */}
              <div className="flex items-center space-x-2 self-start lg:self-auto">
                <button
                  onClick={() => setIsArchAutoPlaying(!isArchAutoPlaying)}
                  className={`inline-flex items-center space-x-1.5 text-xs font-bold px-3 py-1.5 rounded-xl border transition-colors ${
                    isArchAutoPlaying
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20'
                  }`}
                  title={isArchAutoPlaying ? 'Pause Auto-Run' : 'Play Auto-Run'}
                >
                  {isArchAutoPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  <span>{isArchAutoPlaying ? 'Pause Flow' : 'Auto-Run'}</span>
                </button>

                <div className="flex items-center bg-gray-950 border border-gray-800 rounded-xl p-0.5">
                  <button
                    onClick={() => {
                      setIsArchAutoPlaying(false);
                      setActiveArchStep((prev) => (prev - 1 + architectureNodes.length) % architectureNodes.length);
                    }}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800/80 rounded-lg transition-colors"
                    title="Previous Node"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-mono font-bold text-cyan-400 px-2.5">
                    {architectureNodes[activeArchStep]?.step} / 15
                  </span>
                  <button
                    onClick={() => {
                      setIsArchAutoPlaying(false);
                      setActiveArchStep((prev) => (prev + 1) % architectureNodes.length);
                    }}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800/80 rounded-lg transition-colors"
                    title="Next Node"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Architecture Stage Tabs & Tracker Dots */}
            <div className="flex items-center justify-between pt-4 flex-wrap gap-3">
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 max-w-full">
                {architectureStages.map((stage) => {
                  const isActive = activeStageFilter === stage.id;
                  return (
                    <button
                      key={stage.id}
                      onClick={() => {
                        setActiveStageFilter(stage.id);
                        if (stage.id !== 'all') {
                          const firstIdx = architectureNodes.findIndex((n) => n.phase === stage.id);
                          if (firstIdx !== -1) setActiveArchStep(firstIdx);
                        }
                      }}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                          : 'bg-gray-950 text-gray-400 hover:text-gray-200 border border-gray-800/80'
                      }`}
                    >
                      {stage.label}
                    </button>
                  );
                })}
              </div>

              {/* Progress Tracker Dots */}
              <div className="hidden xl:flex items-center space-x-1.5">
                {architectureNodes.map((node, idx) => (
                  <button
                    key={node.id}
                    onClick={() => {
                      setIsArchAutoPlaying(false);
                      setActiveArchStep(idx);
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      activeArchStep === idx
                        ? 'w-6 bg-gradient-to-r from-cyan-400 to-emerald-400 shadow-sm shadow-cyan-400'
                        : 'w-2 bg-gray-800 hover:bg-gray-700'
                    }`}
                    title={`Jump to Step ${node.step}: ${node.title}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Running Architecture White-Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayedNodes.map((node) => {
              const IconComp = node.icon;
              const isCurrentActive = architectureNodes[activeArchStep]?.id === node.id;

              return (
                <div
                  key={node.id}
                  onClick={() => {
                    setIsArchAutoPlaying(false);
                    const originalIdx = architectureNodes.findIndex((n) => n.id === node.id);
                    setActiveArchStep(originalIdx);
                  }}
                  className={`bg-white rounded-2xl p-5 shadow-md cursor-pointer transition-all duration-300 flex flex-col justify-between group ${
                    isCurrentActive
                      ? 'ring-2 ring-cyan-500 border-cyan-400 shadow-xl shadow-cyan-500/20 scale-[1.02]'
                      : 'border border-gray-100 hover:border-gray-300 hover:shadow-lg'
                  }`}
                >
                  <div>
                    {/* Top Row: Icon + Step / Active Status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-11 h-11 rounded-xl ${node.iconBg} border flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <IconComp className="w-5 h-5" />
                      </div>

                      {isCurrentActive ? (
                        <span className="inline-flex items-center text-[10px] font-mono font-bold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-full border border-cyan-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-ping mr-1" />
                          Active Node
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono font-bold text-gray-400">
                          Step {node.step}
                        </span>
                      )}
                    </div>

                    <h3 className="text-base font-extrabold text-gray-900 mb-1.5 leading-snug">
                      {node.title}
                    </h3>
                    <p className="text-xs text-gray-600 leading-relaxed mb-3">
                      {node.desc}
                    </p>
                  </div>

                  <div className="pt-2.5 border-t border-gray-100 flex items-center text-xs font-semibold text-gray-700">
                    <CheckCircle2 className={`w-3.5 h-3.5 mr-1.5 flex-shrink-0 ${isCurrentActive ? 'text-cyan-600' : 'text-emerald-600'}`} />
                    <span>{node.keypoint}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core E-Commerce Capabilities (Curated, High-Relevance 6 Cards) */}
      <section className="py-20 md:py-28 relative z-10 border-b border-gray-800/80 bg-gray-950/60">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-xs font-mono font-bold uppercase tracking-wider">
                CORE PLATFORM CAPABILITIES
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Engineered For Modern <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Digital Commerce</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Every feature designed to eliminate checkout friction, automate warehouse fulfillment, and scale transaction volume seamlessly.
            </p>
          </div>

          {/* Capabilities 6-Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {coreCapabilities.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-900/90 border border-gray-800 rounded-3xl p-7 flex flex-col justify-between shadow-xl"
              >
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center shadow-md`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 rounded-full">
                      {feature.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6 font-normal">
                    {feature.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800 space-y-2">
                  {feature.highlights.map((item, idx) => (
                    <div key={idx} className="flex items-center text-xs text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Supported Tech Platforms Grid - Cohesive Dark Styling */}
      <section className="py-20 md:py-28 relative z-10 border-b border-gray-800/80">
        <div className="container mx-auto px-6">

          <div className="text-center mb-14 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-4 shadow-sm">
              <span className="text-cyan-400 text-xs font-mono font-bold uppercase tracking-wider">
                AGNOSTIC TECH STACK SUPPORT
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Supported E-Commerce <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Platforms & Stacks</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Whether building bespoke Headless Next.js storefronts or scaling high-volume Shopify & Adobe Commerce stores, we support industry-leading architectures.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, idx) => (
              <div key={idx} className="bg-gray-900/90 border border-gray-800 rounded-2xl p-6 text-left shadow-lg flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-orange-400" />
                    </div>
                    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border ${platform.tagColor}`}>
                      {platform.badge}
                    </span>
                  </div>
                  <h4 className="text-base font-extrabold text-white mb-2">{platform.name}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-normal">{platform.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>


      {/* Full-Width Enterprise E-Commerce CTA */}
      <section className="py-20 md:py-28 relative z-10 overflow-hidden">
        <div className="container mx-auto px-6 max-w-4xl text-center">

          <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-orange-400 text-xs font-mono font-bold uppercase tracking-wider">
              READY TO SCALE YOUR ONLINE SALES?
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
            Ready to Build Your <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">High-Velocity Storefront?</span>
          </h2>

          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Schedule a technical consultation with our senior e-commerce architects to discuss your custom storefront requirements, ERP integrations, and growth roadmap.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-500 hover:from-orange-600 hover:to-cyan-600 text-white px-8 py-6 text-base font-extrabold rounded-xl shadow-xl hover:shadow-orange-500/30 transition-all duration-300">
                Talk to E-Commerce Architects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a
              href="https://wa.me/918796118101?text=Hello!%20I%20want%20to%20discuss%20an%20E-Commerce%20project."
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="outline" className="w-full sm:w-auto border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-8 py-6 text-base font-bold rounded-xl flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-400" />
                Talk To Expert (+91 8796 118 101)
              </Button>
            </a>
          </div>

        </div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

    </div>
  );
};

export default EcommerceSolutions;